<script>
import { mapState } from "vuex";
import { api } from "@/state/services";
import { visibleMenuItems } from "@/utils/menu-map";
import { isEmbeddedWebView } from "@/utils/webview";
import OnboardingTour from "@/components/onboarding-tour";

// Prefixo da chave de fallback no localStorage. A chave é por usuário
// (onboarding_done:<id>) para que trocar de conta no mesmo dispositivo não
// esconda o tour de quem ainda não viu.
const LOCAL_KEY_PREFIX = "onboarding_done:";

// Passo em que o usuário parou. Só existe para retomar de onde parou se a
// página recarregar no meio do tour — a fonte de verdade da CONCLUSÃO
// continua sendo a coluna no banco.
const LOCAL_STEP_PREFIX = "onboarding_step:";

// Chaves do catálogo do menu viram seletor CSS (`[data-tour="menu-<key>"]`).
// Elas são constantes do nosso próprio código, mas a validação deixa isso
// explícito: nada que venha do usuário/API entra em querySelector.
const SAFE_KEY = /^[a-z0-9-]+$/;

export default {
  name: "OnboardingModal",
  components: { OnboardingTour },
  data() {
    return {
      showModal: false,
      // Chave (e não índice) do passo atual: a lista de passos é recalculada
      // quando o guard do router recarrega account.user, e um índice poderia
      // apontar para outro passo depois do recálculo.
      stepKey: null,
      // 1 = avançando, -1 = voltando. Usado para decidir para que lado pular
      // quando o alvo de um passo não aparece.
      direction: 1,
      // Marcado quando o usuário conclui/pula nesta sessão. Existe para o
      // caso de o POST falhar: sem isso o tour reabriria a cada navegação,
      // porque o guard do router recarrega account.user (com onboarding_done
      // ainda 'no') a cada rota.
      dismissedLocally: false,
      // Guarda de reentrância: fechar o tour dispara finish() de novo em mais
      // de um caminho. Sem isso o POST sairia duplicado.
      completed: false,
    };
  },
  computed: {
    ...mapState({
      account: (state) => state.account,
    }),
    user() {
      return (this.account && this.account.user) || null;
    },
    replayRequested() {
      return !!(this.account && this.account.onboardingReplay);
    },

    // -----------------------------------------------------------------------
    // Precedência
    // -----------------------------------------------------------------------
    // O tour é o ÚLTIMO da fila. Ele só aparece quando nenhum fluxo obrigatório
    // está em andamento:
    //
    //  1. Redirecionamentos do guard em router/routes.js: /atualizacad,
    //     /documents e /contracts/:id. Enquanto o usuário está preso num
    //     deles, o tour não abre (nem faria sentido: ele ensina um menu que o
    //     usuário ainda não consegue navegar).
    //  2. RegionalCpfModal — modal bloqueante (sem backdrop/esc) para Regional
    //     sem CPF. O tour por cima de um modal bloqueante travaria a tela.
    //  3. AfiliadosRegulamentoModal — idem, disparado por
    //     account.afiliadosRegulamentoPendingPath.
    //
    // Se qualquer um deles voltar a ser verdadeiro, o tour recolhe sozinho e
    // reaparece depois (a flag no banco continua 'no').
    blockedByRequiredFlow() {
      const user = this.user;
      if (!user) return true;

      const path = (this.$route && this.$route.path) || "";

      if (
        path === "/atualizacad" ||
        path === "/documents" ||
        path.indexOf("/contracts/") === 0 ||
        path.indexOf("/login") === 0 ||
        path === "/logout"
      ) {
        return true;
      }

      if (user.atualizacad === "pending") return true;
      if (user.documents === "pending") return true;
      if (user.contracts && user.contracts.length > 0) return true;

      // RegionalCpfModal
      if (Number(user.regional) === 1 && !user.cpf_regional) return true;

      // AfiliadosRegulamentoModal
      if (this.account && this.account.afiliadosRegulamentoPendingPath) return true;

      return false;
    },

    shouldShow() {
      if (this.blockedByRequiredFlow) return false;
      if (this.dismissedLocally) return false;
      if (this.readLocalFlag()) return false;

      return this.user.onboarding_done !== "yes";
    },

    // -----------------------------------------------------------------------
    // Passos
    // -----------------------------------------------------------------------
    // A lista de itens de menu vem de utils/menu-map.js — o MESMO módulo que
    // alimenta os v-if do side-nav.vue. Enquanto as duas telas lerem daqui,
    // o tour não tem como apontar para um item bloqueado ou inexistente:
    // se o item não é visível para aquele usuário, ele nem entra na lista.
    //
    // Além disso, `onboarding-tour.vue` confere o alvo no DOM antes de
    // destacar e avisa (`target-missing`) quando ele não aparece — segunda
    // rede de proteção, para o caso de um item ser escondido por CSS.
    steps() {
      const user = this.user;
      if (!user) return [];

      const firstName = (user.name || "").split(" ")[0] || "";
      const embedded = isEmbeddedWebView();
      const items = visibleMenuItems(user, { embedded }).filter((item) =>
        SAFE_KEY.test(item.key)
      );

      // "Guia do App" é o último item do menu e fica na posição natural dele.
      // O atalho para os vídeos vira o CTA do passo de encerramento — assim a
      // sequência no celular é "abre a gaveta → percorre o menu → fecha a
      // gaveta → perfil → fim", sem o fecha-abre-fecha que existia quando o
      // Guia do App era arrancado do meio do menu para o final.
      const guia = items.find((item) => item.key === "guia") || null;
      const menuItems = items;

      const list = [
        {
          key: "welcome",
          title: firstName ? `Boas-vindas, ${firstName}!` : "Boas-vindas!",
          text:
            "Este é o seu Escritório Virtual. Em menos de um minuto vamos apontar, na própria tela, onde fica cada coisa. Você pode pular quando quiser e rever o tutorial depois pelo botão 'Como funciona', no topo da tela.",
          target: null,
          needsSidebar: false,
        },
      ];

      if (menuItems.length > 0) {
        list.push({
          key: "menu",
          title: "É por aqui que você navega",
          // Texto propositalmente válido nos dois contextos: o alvo é
          // responsivo (botão ☰ no celular, menu inteiro no computador) e o
          // passo não sabe, na hora de montar a lista, em qual dos dois vai
          // cair depois de um resize.
          text:
            "Todas as seções liberadas para o seu perfil ficam neste menu. No computador ele fica à esquerda (e pode ser recolhido pelo botão no topo); no celular, abre pelo botão ☰ no topo da tela. Vamos passar item por item.",
          // Alvo responsivo: no computador destacamos a sidebar inteira; no
          // celular ela está fechada (invisível) e o candidato visível passa a
          // ser o botão ☰. Quem escolhe é o componente do tour, pegando o
          // primeiro candidato realmente visível — por isso a sidebar vem
          // primeiro (o botão agora existe também no desktop, para recolher).
          target: ["#sidebar-menu", '[data-tour="menu-toggle"]'],
          needsSidebar: false,
        });

        menuItems.forEach((item) => {
          list.push({
            key: "menu-" + item.key,
            title: item.title,
            text: item.description,
            target: '[data-tour="menu-' + item.key + '"]',
            // No celular a gaveta precisa estar aberta para o item existir na
            // tela; o componente do tour abre e fecha sozinho.
            needsSidebar: true,
          });
        });
      }

      list.push({
        key: "busca",
        title: "Busca rápida",
        text:
          "Procurando uma tela? Digite o nome aqui (ou aperte Ctrl K). A busca só mostra o que está liberado para o seu perfil.",
        target: '[data-tour="busca"]',
        needsSidebar: false,
      });

      list.push({
        key: "perfil",
        title: "Seu perfil",
        text:
          "No seu avatar, aqui no canto, você atualiza os seus dados, o seu endereço e a sua senha. Ao lado ficam as notificações e o botão de modo claro/escuro.",
        target: '[data-tour="perfil"]',
        needsSidebar: false,
      });

      list.push({
        key: "done",
        title: "Tudo pronto!",
        text: guia
          ? "É isso. Se quiser ver cada tela com calma, o Guia do App tem vídeos curtos com o passo a passo. E este tutorial continua disponível no botão 'Como funciona', no topo da tela. Bom trabalho!"
          : "É isso. Se precisar rever este tutorial, ele está no botão 'Como funciona', no topo da tela. Bom trabalho!",
        target: null,
        needsSidebar: false,
        // Navegação só para uma rota comprovadamente liberada: o CTA só existe
        // quando o item "Guia do App" está visível para este usuário.
        cta: guia ? { label: "Abrir o Guia do App", to: "/guia-do-app" } : null,
      });

      return list;
    },
    totalSteps() {
      return this.steps.length;
    },
    stepIndex() {
      if (!this.stepKey) return 0;
      const index = this.steps.findIndex((item) => item.key === this.stepKey);
      return index >= 0 ? index : 0;
    },
    currentStep() {
      return this.steps[this.stepIndex] || null;
    },
    isLastStep() {
      return this.stepIndex >= this.totalSteps - 1;
    },
    tourVisible() {
      return this.showModal && !!this.currentStep;
    },
  },
  watch: {
    shouldShow: {
      immediate: true,
      handler(val) {
        if (val) {
          this.open();
        } else if (!this.replayRequested) {
          this.showModal = false;
        }
      },
    },
    replayRequested(val) {
      // A precedência vale também para o replay: se um fluxo obrigatório
      // estiver em cena, o pedido é simplesmente descartado (em vez de abrir
      // o tour por cima de um modal bloqueante).
      if (val && !this.blockedByRequiredFlow) {
        this.completed = false;
        this.open(true);
      } else if (val) {
        this.$store.commit("account/setOnboardingReplay", false);
      }
    },
    // Se um fluxo obrigatório entrar em cena com o tour aberto (por exemplo o
    // modal do CPF do Regional), o tour recolhe sem marcar conclusão: a flag
    // no banco continua 'no' e ele volta no próximo acesso.
    blockedByRequiredFlow(val) {
      if (val && this.showModal) {
        this.showModal = false;
      }
    },
  },
  methods: {
    // A chave inclui o id da secretária quando a sessão é de regional_user:
    // nesse caso `user.id` é o da Regional titular, e usar só ele faria a
    // secretária "consumir" o tour da chefe no mesmo dispositivo.
    localKey(prefix) {
      const user = this.user;
      if (!user || !user.id) return null;

      const regionalUserId =
        user.regional_user && user.regional_user.id ? ":ru" + user.regional_user.id : "";

      return prefix + user.id + regionalUserId;
    },
    readLocalFlag() {
      const key = this.localKey(LOCAL_KEY_PREFIX);
      if (!key) return false;
      try {
        return localStorage.getItem(key) === "yes";
      } catch (e) {
        return false;
      }
    },
    writeLocalFlag() {
      const key = this.localKey(LOCAL_KEY_PREFIX);
      if (!key) return;
      try {
        localStorage.setItem(key, "yes");
      } catch (e) {
        // Modo privado / storage cheio: seguimos sem o fallback. A fonte de
        // verdade continua sendo o banco.
      }
    },
    // Retomada: guardamos a CHAVE do passo, não o índice. A lista de passos
    // depende dos módulos/permissões do usuário e pode mudar entre um acesso
    // e outro; uma chave que não existe mais simplesmente é ignorada.
    persistStep() {
      const key = this.localKey(LOCAL_STEP_PREFIX);
      if (!key) return;
      try {
        if (this.stepKey) {
          localStorage.setItem(key, this.stepKey);
        } else {
          localStorage.removeItem(key);
        }
      } catch (e) {
        // Sem retomada; o tour recomeça do início. Não é erro para o usuário.
      }
    },
    readPersistedStep() {
      const key = this.localKey(LOCAL_STEP_PREFIX);
      if (!key) return null;
      try {
        return localStorage.getItem(key);
      } catch (e) {
        return null;
      }
    },
    clearPersistedStep() {
      const key = this.localKey(LOCAL_STEP_PREFIX);
      if (!key) return;
      try {
        localStorage.removeItem(key);
      } catch (e) {
        // Ignorado de propósito.
      }
    },

    open(fromStart) {
      this.direction = 1;

      const saved = fromStart ? null : this.readPersistedStep();
      const exists = saved && this.steps.some((item) => item.key === saved);

      this.stepKey = exists ? saved : null;
      if (fromStart) this.clearPersistedStep();

      this.showModal = true;
    },
    goToIndex(index) {
      if (this.totalSteps === 0) return;

      const clamped = Math.min(Math.max(index, 0), this.totalSteps - 1);
      const step = this.steps[clamped];

      this.stepKey = step ? step.key : null;
      this.persistStep();
    },
    next() {
      if (this.isLastStep) {
        this.finish();
        return;
      }
      this.direction = 1;
      this.goToIndex(this.stepIndex + 1);
    },
    previous() {
      if (this.stepIndex === 0) return;
      this.direction = -1;
      this.goToIndex(this.stepIndex - 1);
    },
    // O alvo não apareceu no tempo esperado: pulamos o passo mantendo o
    // sentido da navegação. Como o índice só anda para um lado por vez, isto
    // termina sempre — o usuário nunca fica preso.
    onTargetMissing(key) {
      if (!this.currentStep || this.currentStep.key !== key) return;

      const target = this.stepIndex + this.direction;

      if (target >= this.totalSteps) {
        this.finish();
        return;
      }

      if (target < 0) {
        this.direction = 1;
        this.goToIndex(0);
        return;
      }

      this.goToIndex(target);
    },
    openCta() {
      const step = this.currentStep;
      const to = step && step.cta ? step.cta.to : null;

      this.finish();

      if (to) {
        this.$router.push(to).catch(() => {});
      }
    },

    // Fecha o tour e registra a conclusão.
    //
    // A ordem importa: primeiro fechamos e marcamos na sessão, só depois
    // chamamos a API. Assim, se o POST falhar (rede, API fora), o usuário não
    // fica preso no tour nem o vê de novo a cada navegação.
    //
    // O `dismissedLocally` (memória) sempre é setado; a chave persistente no
    // localStorage só é gravada quando a API CONFIRMA a persistência. Se a
    // API falhou, o banco continua em 'no' e o tour reaparece no próximo
    // acesso — que é o comportamento correto, já que o banco é a fonte de
    // verdade. Gravar a chave local mesmo assim faria o usuário perder o tour
    // em definitivo naquele dispositivo.
    finish() {
      if (this.completed) {
        this.showModal = false;
        return;
      }
      this.completed = true;

      this.showModal = false;
      this.dismissedLocally = true;
      this.clearPersistedStep();

      this.$store.commit("account/setOnboardingReplay", false);

      if (this.user && this.user.onboarding_done === "yes") {
        return;
      }

      // Sessão de secretária (regional_user): a API responde sucesso mas NÃO
      // persiste, porque o ID do JWT é o da Regional titular — marcar ali
      // roubaria o tour dela. Nesse caso o "não mostrar de novo" é local.
      const isRegionalUserSession = !!(this.user && this.user.regional_user);

      // Sem estado de "salvando" na interface de propósito: o tour já foi
      // fechado antes desta chamada, então não há nada para desabilitar ou
      // mostrar enquanto ela acontece.
      //
      // Endpoint já existente (POST user/onboarding → User::UserOnboardingDone_post
      // → Model_users::markOnboardingDone). Autenticado por x-auth-token, então
      // não precisa de entrada em config/rest.php; já consta no $alwaysAllowed
      // do Model App para as sessões de secretária.
      api
        .post("user/onboarding")
        .then((response) => {
          if (!response.data || response.data.status !== "success") {
            return;
          }

          this.writeLocalFlag();

          if (isRegionalUserSession || response.data.persisted === false) {
            return;
          }

          this.$store.commit("account/setOnboardingDone");
        })
        .catch(() => {
          // Silencioso de propósito: o tour é opcional e já foi encerrado na
          // interface. Não há nada de útil para o usuário fazer com o erro, e
          // não gravamos a flag local — o tour volta no próximo acesso.
        });
    },
  },
};
</script>

<template>
  <!--
    Wrapper vazio: o overlay do tour se move para o <body> no mounted() dele
    (ver onboarding-tour.vue) e este nó fica só como âncora do v-if, sem
    ocupar espaço no layout.
  -->
  <div class="onboarding-anchor">
    <OnboardingTour
      v-if="tourVisible"
      :step="currentStep"
      :index="stepIndex"
      :total="totalSteps"
      :is-last="isLastStep"
      @next="next"
      @previous="previous"
      @skip="finish"
      @cta="openCta"
      @target-missing="onTargetMissing"
    />
  </div>
</template>
